import { createContext, useContext, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Avatar, Box, Group, Stack, Text, Tooltip } from '@mantine/core'
import {
  ANALYTICS_TOOLTIP_WIDTH,
  analyticsTooltipStyles,
  formatDurationClock,
  formatNumber,
  formatTooltipPercent,
  initials,
  type ChartRowsTooltipData,
  type ProductTooltipData,
} from '@/pages/analytics/analytics-new-utils'

export type AnalyticsTooltipContextValue = {
  activeKey: string | null
  setActiveKey: (key: string | null) => void
}

export const AnalyticsTooltipContext = createContext<AnalyticsTooltipContextValue | null>(null)

export function AnalyticsTooltipProvider({ children }: { children: ReactNode }) {
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const value = useMemo(() => ({ activeKey, setActiveKey }), [activeKey])
  return <AnalyticsTooltipContext.Provider value={value}>{children}</AnalyticsTooltipContext.Provider>
}

export function UnifiedTooltip({
  label,
  children,
  styles = analyticsTooltipStyles,
  wrapperStyle,
  multiline = true,
  withArrow = true,
  withinPortal = true,
  zIndex = 5000,
  maw,
}: {
  label: ReactNode
  children: ReactNode
  styles?: Record<string, unknown>
  wrapperStyle?: CSSProperties
  multiline?: boolean
  withArrow?: boolean
  withinPortal?: boolean
  zIndex?: number
  maw?: number
}) {
  const tooltipContext = useContext(AnalyticsTooltipContext)
  const tooltipKeyRef = useRef<string | null>(null)
  if (!tooltipKeyRef.current) {
    tooltipKeyRef.current = `analytics-tooltip-${Math.random().toString(36).slice(2)}`
  }
  const tooltipKey = tooltipKeyRef.current
  const close = () => {
    if (!tooltipContext || tooltipContext.activeKey === tooltipKey) {
      tooltipContext?.setActiveKey(null)
    }
  }
  const open = () => tooltipContext?.setActiveKey(tooltipKey)

  return (
    <Tooltip
      label={label}
      styles={styles}
      multiline={multiline}
      withArrow={withArrow}
      withinPortal={withinPortal}
      zIndex={zIndex}
      maw={maw}
    >
      <Box
        component="span"
        onMouseEnter={open}
        onMouseOver={open}
        onMouseLeave={close}
        onPointerEnter={open}
        onPointerOver={open}
        onPointerLeave={close}
        onFocus={open}
        onBlur={close}
        style={wrapperStyle}
      >
        {children}
      </Box>
    </Tooltip>
  )
}

export function AnalyticsTooltipContent({ title, rows }: { title: string; rows: Array<{ label: string; value: string; tone?: 'primary' | 'muted' | 'default' }> }) {
  return (
    <Stack gap={8} style={{ width: '100%', minWidth: 0, maxWidth: ANALYTICS_TOOLTIP_WIDTH - 24 }}>
      <Text style={{ fontSize: 12, lineHeight: '15px', fontWeight: 700, color: '#ffffff' }}>{title}</Text>
      <Stack gap={0}>
        {rows.map((row) => {
          const isLongValue = row.value.length > 34
          return (
            <Stack
              key={`${row.label}-${row.value}`}
              gap={4}
              style={{
                minHeight: 28,
                padding: '6px 0',
                borderBottom: '1px solid rgba(255, 255, 255, 0.07)',
              }}
            >
              <Group justify="space-between" gap={14} wrap="nowrap" style={{ minWidth: 0 }}>
                <Text style={{ minWidth: 0, fontSize: 12, lineHeight: '16px', fontWeight: 500, color: row.tone === 'muted' ? '#C4CBD5' : '#F8FAFC', whiteSpace: 'normal' }}>
                  {row.label}
                </Text>
                {!isLongValue ? (
                  <Text style={{ flex: '0 0 auto', maxWidth: 160, fontSize: 12, lineHeight: '16px', fontWeight: 800, color: row.tone === 'primary' ? '#5B95FF' : row.tone === 'muted' ? '#C4CBD5' : '#FFFFFF', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                    {row.value}
                  </Text>
                ) : null}
              </Group>
              {isLongValue ? (
                <Text style={{ fontSize: 12, lineHeight: '16px', fontWeight: 700, color: row.tone === 'primary' ? '#5B95FF' : row.tone === 'muted' ? '#C4CBD5' : '#FFFFFF', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                  {row.value}
                </Text>
              ) : null}
            </Stack>
          )
        })}
      </Stack>
    </Stack>
  )
}

export function ProductTooltipRow({ label, value, tone = 'default' }: { label: ReactNode; value: string; tone?: 'blue' | 'muted' | 'danger' | 'default' }) {
  const valueColor = tone === 'blue' ? '#5B95FF' : tone === 'muted' ? '#C4CBD5' : tone === 'danger' ? '#FF7043' : '#FFFFFF'
  const labelColor = tone === 'danger' ? '#FF7043' : '#F8FAFC'
  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      style={{
        minHeight: 30,
        borderBottom: '1px solid rgba(255, 255, 255, 0.07)',
      }}
    >
      <Text style={{ minWidth: 0, fontSize: 12, lineHeight: '16px', fontWeight: 500, color: labelColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</Text>
      <Text style={{ flex: '0 0 auto', maxWidth: 150, fontSize: 12, lineHeight: '16px', fontWeight: 800, color: valueColor, fontVariantNumeric: 'tabular-nums', textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</Text>
    </Group>
  )
}

export function ProductChartTooltipContent({ data }: { data: ProductTooltipData }) {
  return (
    <Box
      style={{
        width: ANALYTICS_TOOLTIP_WIDTH,
        maxWidth: 'calc(100vw - 32px)',
        borderRadius: 12,
        padding: '10px 12px',
        background: '#303030',
        border: '1px solid rgba(255, 255, 255, 0.06)',
        boxShadow: '0 16px 36px rgba(0, 0, 0, 0.28)',
        color: '#fff',
      }}
    >
      <Group gap={10} wrap="nowrap" mb={10}>
        <Box style={{ width: 18, height: 18, flex: '0 0 18px', borderRadius: 999, background: data.color }} />
        <Text style={{ fontSize: 14, lineHeight: '18px', fontWeight: 800, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{data.name}</Text>
      </Group>
      <Stack gap={0}>
        <ProductTooltipRow label="Обращений" value={`${formatNumber(data.total)} (${formatTooltipPercent(data.percent)})`} tone="blue" />
        <ProductTooltipRow label="Пред. период" value={formatNumber(data.previous)} tone="muted" />
        <ProductTooltipRow label="Открыто" value={formatNumber(data.opened)} />
        <ProductTooltipRow label="В работе" value={formatNumber(data.inWork)} />
        <ProductTooltipRow label="Решено" value={formatNumber(data.resolved)} />
        <ProductTooltipRow label="Закрыто" value={formatNumber(data.closed)} />
        <Box h={8} />
        <ProductTooltipRow label="Ответ" value={formatDurationClock(data.avgFirstResponseMinutes)} />
        <ProductTooltipRow label="Закрытие" value={formatDurationClock(data.avgCloseMinutes)} />
        <Box h={8} />
        <ProductTooltipRow label="⚠ Аварии" value={formatNumber(data.emergency)} tone="danger" />
      </Stack>
    </Box>
  )
}

export function ChartRowsTooltipContent({ data }: { data: ChartRowsTooltipData }) {
  return (
    <Box
      style={{
        width: ANALYTICS_TOOLTIP_WIDTH,
        maxWidth: 'calc(100vw - 32px)',
        borderRadius: 12,
        padding: '10px 12px',
        background: '#303030',
        border: '1px solid rgba(255, 255, 255, 0.06)',
        boxShadow: '0 16px 36px rgba(0, 0, 0, 0.28)',
        color: '#fff',
      }}
    >
      <Group gap={10} wrap="nowrap" mb={10}>
        {data.imageUrl || data.emoji ? (
          <Avatar
            src={data.imageUrl}
            size={22}
            radius="xl"
            style={{
              background: data.color || '#3B82F6',
              color: '#fff',
              flex: '0 0 22px',
              fontSize: 9,
              fontWeight: 800,
            }}
          >
            {data.emoji || initials(data.title)}
          </Avatar>
        ) : data.color ? (
          <Box style={{ width: 18, height: 18, flex: '0 0 18px', borderRadius: 999, background: data.color }} />
        ) : null}
        <Text style={{ minWidth: 0, fontSize: 14, lineHeight: '18px', fontWeight: 800, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{data.title}</Text>
      </Group>
      <Stack gap={0}>
        {data.rows.map((row, index) => (
          <ProductTooltipRow key={`${index}-${String(row.label)}`} label={row.label} value={row.value} tone={row.tone} />
        ))}
      </Stack>
    </Box>
  )
}

